import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, AlertTriangle, Save, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import {
  platformSettingsApi, PlatformSettings as PlatformSettingsData,
} from "@/lib/platform-api";
import { ApiError } from "@/lib/api";

/** One enforced/persisted platform flag. */
const FLAGS: { key: keyof PlatformSettingsData; label: string; help: string; enforced: boolean }[] = [
  { key: "allowNewTenantSignups", label: "Allow new tenant signups", help: "When off, no new tenant can be created — even by a super admin.", enforced: true },
  { key: "maintenanceMode", label: "Maintenance mode", help: "When on, only super admins can use the platform; everyone else gets a 503.", enforced: true },
  { key: "aiServiceEnabled", label: "AI service", help: "Global switch for the AI assistant (takes effect once AI is live).", enforced: false },
  { key: "notificationServiceEnabled", label: "Notification service", help: "Global switch for outbound notifications.", enforced: false },
  { key: "integrationsEnabled", label: "Integrations", help: "Global switch for third-party integrations.", enforced: false },
];

export default function PlatformSettings() {
  const [data, setData] = useState<PlatformSettingsData | null>(null);
  const [productName, setProductName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    platformSettingsApi.get()
      .then((s) => {
        if (cancelled) return;
        setData(s);
        setProductName(String((s.customization as Record<string, unknown> | null)?.productName ?? ""));
        setError(null);
      })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : "Failed to load settings"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const setFlag = (key: keyof PlatformSettingsData, value: boolean) =>
    setData((d) => (d ? { ...d, [key]: value } : d));

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const updated = await platformSettingsApi.update({
        maintenanceMode: data.maintenanceMode,
        allowNewTenantSignups: data.allowNewTenantSignups,
        aiServiceEnabled: data.aiServiceEnabled,
        notificationServiceEnabled: data.notificationServiceEnabled,
        integrationsEnabled: data.integrationsEnabled,
        customization: { ...(data.customization ?? {}), productName: productName.trim() || undefined },
      });
      setData(updated);
      toast.success("Platform settings saved");
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex h-96 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }
  if (error && !data) {
    return (
      <Card className="flex flex-col items-center justify-center gap-3 p-12 text-center">
        <AlertTriangle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button size="sm" onClick={() => window.location.reload()}>Try again</Button>
      </Card>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Platform settings</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Global switches for the whole platform. Enforced flags take effect immediately.</p>
      </div>

      {data.maintenanceMode && (
        <div className="flex items-center gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          <ShieldAlert className="h-4 w-4 flex-none" />
          Maintenance mode is <b>on</b> — only super admins can use the platform right now.
        </div>
      )}

      <Card className="divide-y">
        {FLAGS.map((f) => (
          <div key={f.key} className="flex items-center justify-between gap-4 p-4">
            <div>
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">{f.label}</Label>
                {f.enforced
                  ? <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">Enforced</span>
                  : <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Persisted</span>}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">{f.help}</p>
            </div>
            <Switch checked={Boolean(data[f.key])} onCheckedChange={(v) => setFlag(f.key, v)} />
          </div>
        ))}
        <div className="p-4">
          <Label className="text-sm font-medium">Product name</Label>
          <p className="mb-2 mt-0.5 text-xs text-muted-foreground">Branding label stored with the platform (customization).</p>
          <Input value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="GeoKerja" className="max-w-xs" />
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save changes
        </Button>
      </div>
    </div>
  );
}
